import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { isAppMode } from "@/lib/appMode";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";
import { fireFeaturedToast } from "@/components/FeaturedCelebration";
import { useLiveNotificationSound } from "@/hooks/useLiveNotificationSound";
import { useMenuLayout } from "@/lib/useMenuLayout";
import { pathToRef, saveAppRef, buildPlayStoreUrl } from "@/lib/appRef";
import { FanChoiceIcon } from "@/components/FanChoiceIcon";

const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL;

// Microphone Icon Component
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

// Bell Icon Component
const BellIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

// Notification type icons
const getNotificationIcon = (type) => {
  switch (type) {
    case 'like': return '❤️';
    case 'comment': return '💬';
    case 'follow': return '👋';
    case 'featured': return '🎖️';
    case 'follower_featured': return '🎖️';
    case 'milestone': return '🏆';
    case 'studio_invite':
    case 'studio_invite_accepted':
    case 'studio_invite_declined': return '🎙️';
    case 'crew_invite': return '🤝';
    case 'crew_join_request': return '🛎️';
    case 'crew_request_approved': return '✅';
    case 'crew_announcement': return '📣';
    case 'crew_mention': return '🏷️';
    case 'crew_reply': return '↩️';
    case 'crew_post': return '🎙️';
    case 'referral': return '🎉';
    case 'reward': return '🪙';
    case 'report': return '📰';
    // Phase 21.1e — Live notification family.
    case 'live_start': return '🎤';
    case 'live_gift_received': return '🎁';
    case 'beam_request': return '🙋';
    case 'beam_approved': return '✅';
    case 'beam_declined': return '🙏';
    case 'mod_muted': return '🔇';
    case 'mod_kicked':
    case 'mod_removed_from_beam': return '👋';
    case 'mod_banned': return '🚫';
    case 'mod_promoted': return '🎛️';
    case 'mod_demoted': return '⬇️';
    case 'live_reminder': return '⏰';
    default: return '🔔';
  }
};

// Hamburger Menu Icon
const MenuIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// Close Icon
const CloseIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Format relative time
const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

/**
 * Shared Navigation Component with Notifications
 */
const SharedNavigation = ({ currentUser = null, activePage = null }) => {
  const { play: playLiveSound } = useLiveNotificationSound();
  const { items: menuItems, adminButtons } = useMenuLayout();

  // Phase 1A.1 · Full Menu Coverage — helpers to slice the config
  // per section so the mobile drawer renders the same set of rows
  // the CEO configured in the Menu Organizer (no more hardcoded
  // duplicate blocks). Sensitive money/audit destinations (Payouts,
  // Audit) and non-route items (Tutorials, Delete Account) are
  // rendered separately below and stay hardcoded.
  const inSection = (section) =>
    (menuItems || []).filter(
      (it) => it.section === section && it.visible !== false && it.mobile !== false
    ).filter((it) => {
      // Contest Judging Phase 1 — /api/menu-layout sanitizes unknown
      // fields, so `requiresJudgeAssignment` never survives the wire.
      // Gate by the well-known id instead.
      const isJudgeGate = it.requiresJudgeAssignment || it.id === "judge-inbox";
      return !isJudgeGate || hasJudgeAssignments;
    });
  const mobileTestIdFor = (it) => {
    if (it.id === "hall-of-champions") return "mobile-nav-hall-of-champions";
    return it.testid && it.testid.startsWith("nav-")
      ? `mobile-${it.testid}`
      : `mobile-nav-${it.id}`;
  };
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coinBalance, setCoinBalance] = useState(null);
  const [showAdminGroup, setShowAdminGroup] = useState(false);
  // Phase 1 Contest Judging — session flag that controls visibility of the
  // "My Judging" nav row (reuses GET /api/judge/contests; no new endpoint).
  const [hasJudgeAssignments, setHasJudgeAssignments] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Phase 9.7.13 — Defensive role+badge derivation. We accept admin/
  // moderator roles, founder/admin badges, AND the "ar" badge (for A&R
  // users whose role wasn't explicitly flipped via the canonical
  // role-change endpoint). Mirrors the backend `get_ar_or_admin`
  // defensiveness shipped in 9.7.8.
  const _userBadges = currentUser?.badges || [];
  const isFullAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'moderator' ||
    _userBadges.includes('admin') ||
    _userBadges.includes('founder');
  const isAR =
    currentUser?.role === 'ar' || _userBadges.includes('ar');
  const isAdmin = isFullAdmin || isAR; // back-compat flag used below

  // Phase 9.6.1 — Featured Celebration Push: remember which `follower_featured`
  // notification IDs we've already toasted so we never double-fire while
  // polling every 30s. Persisted to localStorage so a page reload doesn't
  // re-toast the same notification.
  const featuredSeenRef = useRef(null);
  if (featuredSeenRef.current === null) {
    try {
      const raw = window.localStorage.getItem("rgm.featured.seen");
      featuredSeenRef.current = new Set(raw ? JSON.parse(raw) : []);
    } catch (_e) {
      featuredSeenRef.current = new Set();
    }
  }
  const rememberFeaturedSeen = (id) => {
    if (!id) return;
    featuredSeenRef.current.add(id);
    try {
      // Cap at most recent 200 ids to keep storage tiny.
      const arr = Array.from(featuredSeenRef.current).slice(-200);
      window.localStorage.setItem("rgm.featured.seen", JSON.stringify(arr));
    } catch (_e) { /* ignore quota errors */ }
  };
  const pollForFeatured = async () => {
    if (!currentUser) return;
    try {
      const r = await api.get('/notifications?limit=10');
      const list = r.data?.notifications || [];
      const fresh = list.filter(
        (n) =>
          n.type === 'follower_featured' &&
          !n.is_read &&
          !featuredSeenRef.current.has(n.notification_id)
      );
      // Newest first comes naturally from backend; toast the top 2 max so
      // we don't blast the user if they were away while many fires happened.
      fresh.slice(0, 2).forEach((n) => {
        fireFeaturedToast({ notification: n, navigate });
        rememberFeaturedSeen(n.notification_id);
      });
      // Also remember any older ones so we never toast them later.
      fresh.slice(2).forEach((n) => rememberFeaturedSeen(n.notification_id));
    } catch (_e) { /* silent */ }
  };

  // Phase 1 Contest Judging + Phase 2 Spotlight A&R — fetch both
  // assignment inboxes once when currentUser is present. Either one
  // returning items is enough to show the "⚖️ My Judging" row. Both
  // return 404 when their respective feature flag is off, and 401 for
  // anonymous users — all silently keep the row hidden.
  useEffect(() => {
    if (!currentUser?.user_id) { setHasJudgeAssignments(false); return; }
    let cancelled = false;
    Promise.allSettled([
      api.get("/judge/contests").then((r) => (r.data?.contests || []).length),
      api.get("/ar/spotlights").then((r) => (r.data?.spotlights || []).length),
    ])
      .then((results) => {
        if (cancelled) return;
        const total = results.reduce((acc, p) => acc + (p.status === "fulfilled" ? p.value : 0), 0);
        setHasJudgeAssignments(total > 0);
      })
      .catch(() => { if (!cancelled) setHasJudgeAssignments(false); });
    return () => { cancelled = true; };
  }, [currentUser?.user_id]);

  // CEO Menu Organizer override — the item MUST vanish if an admin flips
  // its `visible` flag off, even when the user has assignments.
  const _judgeItem = (menuItems || []).find((it) => it.id === "judge-inbox");
  const showJudgeCta = hasJudgeAssignments && (!_judgeItem || _judgeItem.visible !== false);

  // Fetch unread count periodically
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchUnreadCount = async () => {
      try {
        const response = await api.get('/notifications/unread-count');
        setUnreadCount(response.data.unread_count);
      } catch (error) {
        // Silently fail - user may not be logged in
      }
    };
    
    fetchUnreadCount();
    pollForFeatured();
    const interval = setInterval(() => {
      fetchUnreadCount();
      pollForFeatured();
    }, 30000); // Phase 21.1e — polling fallback when sockets are disconnected.
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Phase 21.1e — Real-time notification WebSocket. Sockets are the
  // primary delivery channel; polling above is the fallback. Auto-
  // reconnects with backoff; resilient to backend restarts.
  useEffect(() => {
    if (!currentUser) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;
    let ws = null;
    let reconnectTimer = null;
    let attempts = 0;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        if (!base) return;
        ws = new WebSocket(`${base}/api/notifications/ws?token=${encodeURIComponent(token)}`);
        ws.onopen = () => { attempts = 0; };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg?.type === "notification.new") {
              setUnreadCount((c) => c + 1);
              // Phase 21.1g — sound + vibration for live activity.
              const t = msg.notification?.type || "";
              if (t.startsWith("live_gift") || t === "live_gift_received") {
                playLiveSound("gift");
              } else if (t === "beam_request" || t === "beam_approved" || t === "beam_declined") {
                playLiveSound("beam");
              } else if (t === "live_start") {
                playLiveSound("live");
              } else if (t.startsWith("mod_") || t === "live_reminder") {
                playLiveSound("default");
              }
              // If dropdown is open, also splice the new notif in.
              setNotifications((cur) => {
                if (!cur || cur.length === 0) return cur;
                if (cur.find((n) => n.notification_id === msg.notification.notification_id)) {
                  return cur.map((n) => n.notification_id === msg.notification.notification_id ? msg.notification : n);
                }
                return [msg.notification, ...cur].slice(0, 20);
              });
            }
          } catch (_e) { /* malformed */ }
        };
        ws.onclose = () => {
          if (cancelled) return;
          attempts += 1;
          const delay = Math.min(30000, 1000 * Math.pow(2, attempts));
          reconnectTimer = setTimeout(connect, delay);
        };
        ws.onerror = () => { try { ws && ws.close(); } catch (_e) {} };
      } catch (_e) { /* failed to open */ }
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch (_e) {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Wallet balance chip — poll every 60s when logged in
  useEffect(() => {
    if (!currentUser) { setCoinBalance(null); return; }
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const r = await api.get('/wallet/me');
        if (cancelled) return;
        const w = r.data || {};
        const total = (w.coin_balance ?? 0) + (w.reward_balance ?? 0);
        setCoinBalance(total);
      } catch (_e) { /* silent */ }
    };
    fetchBalance();
    const id = setInterval(fetchBalance, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [currentUser]);

  // Fetch notifications when dropdown opens
  const fetchNotifications = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const response = await api.get('/notifications?limit=10');
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = () => {
    if (!showDropdown) {
      fetchNotifications();
    }
    setShowDropdown(!showDropdown);
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkClass = (page) => {
    const baseClass = "transition-colors font-medium";
    if (activePage === page) {
      return `${baseClass} text-white border-b-2 border-purple-500 pb-1`;
    }
    return `${baseClass} text-gray-300 hover:text-white`;
  };

  // Get link for notification
  const getNotificationLink = (notification) => {
    if (notification.submission_id) {
      return `/track/${notification.submission_id}`;
    }
    if (notification.from_user_id && notification.type === 'follow') {
      return `/artist/${notification.from_user_id}`;
    }
    return '/notifications';
  };

  return (
    <>
    <nav className="fixed left-0 right-0 z-[55] bg-black/95 backdrop-blur-xl border-b border-purple-900/30" style={{ top: "calc(var(--rgm-banner-h, 0px) + var(--rgm-app-banner-h, 0px))" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img 
              src="/rgm-logo.png" 
              alt="RGM Studio" 
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Navigation Links — Feb 2026 CEO Menu Organizer configurable.
              menuItems is the DEFAULT shipped nav when no config is
              saved, so the desktop row remains byte-identical to the
              pre-organizer version out of the box. Each item preserves
              its original data-testid (nav-home, nav-explore, ...) so
              every existing test remains valid. Configuration is
              presentation-only — role gates still live below. */}
          <div className="hidden md:flex items-center gap-6" data-testid="nav-desktop-main">
            <Link to="/fan-vote" className="text-pink-200 hover:text-white font-bold whitespace-nowrap" data-testid="nav-fan-vote"><span className="inline-flex items-center gap-1.5"><FanChoiceIcon size="sm" testId="nav-fan-vote-icon" />Fan Vote</span></Link>
            {menuItems
              .filter((it) => it.visible !== false && it.desktop !== false)
              .filter((it) => {
                const isJudgeGate = it.requiresJudgeAssignment || it.id === "judge-inbox";
                return !isJudgeGate || hasJudgeAssignments;
              })
              .map((it) => {
                // Live tab keeps the animated dot instead of an emoji.
                const iconNode = it.id === "live"
                  ? <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  : (it.icon ? <span>{it.icon}</span> : null);
                return (
                  <Link
                    key={it.id}
                    to={it.route}
                    className={`${linkClass(it.id)} flex items-center gap-${it.id === "live" ? "2" : "1"}`}
                    data-testid={it.testid}
                  >
                    {iconNode}
                    {it.label}
                  </Link>
                );
              })}

            {/* Permanent Get the App link — independent of the banner. */}
            {PLAY_STORE_URL && (
              <a
                href={buildPlayStoreUrl(PLAY_STORE_URL, { ref: pathToRef(location.pathname), path: location.pathname, medium: "nav_menu" })}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => saveAppRef({ ref: pathToRef(location.pathname), path: location.pathname })}
                className={`${linkClass("get-the-app")} flex items-center gap-1`}
                data-testid="nav-get-the-app"
                title="Get the App"
                aria-label="Get the App"
              >
                <img src="/rgm-play-icon.png" alt="" className="w-4 h-4 rounded" />
                <span className="hidden xl:inline">Get the App</span>
              </a>
            )}

            {/* User Actions */}
            {currentUser && !currentUser._loading ? (
              <div className="flex items-center gap-3">
                {/* Notification Bell */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={toggleDropdown}
                    className="relative p-2 text-gray-300 hover:text-white transition-colors"
                    data-testid="notification-bell"
                  >
                    <BellIcon className="w-6 h-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showDropdown && (
                    <Card
                      className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto"
                      style={{ background: 'rgba(26, 26, 30, 0.98)', borderColor: 'rgba(138, 43, 226, 0.5)' }}
                      data-testid="notification-dropdown"
                    >
                      <div className="p-3 border-b border-purple-900/30 flex items-center justify-between">
                        <h3 className="font-bold text-white">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-purple-400 hover:text-purple-300"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>

                      {loading ? (
                        <div className="p-4 text-center text-gray-400">Loading...</div>
                      ) : notifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-400">No notifications yet</div>
                      ) : (
                        <div className="divide-y divide-purple-900/20">
                          {notifications.map((notif) => (
                            <Link
                              key={notif.notification_id}
                              to={getNotificationLink(notif)}
                              onClick={() => {
                                if (!notif.is_read) markAsRead(notif.notification_id);
                                setShowDropdown(false);
                              }}
                              className={`block p-3 hover:bg-purple-900/20 transition-colors ${!notif.is_read ? 'bg-purple-900/10' : ''}`}
                              data-testid={`notification-item-${notif.notification_id}`}
                            >
                              <div className="flex items-start gap-3">
                                <span className="text-xl">{getNotificationIcon(notif.type)}</span>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${!notif.is_read ? 'text-white font-medium' : 'text-gray-300'}`}>
                                    {notif.message}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(notif.created_at)}</p>
                                </div>
                                {!notif.is_read && (
                                  <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0 mt-2"></span>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}

                      <div className="p-2 border-t border-purple-900/30">
                        <Link
                          to="/notifications"
                          onClick={() => setShowDropdown(false)}
                          className="block text-center text-sm text-purple-400 hover:text-purple-300 py-1"
                        >
                          View all notifications
                        </Link>
                      </div>
                    </Card>
                  )}
                </div>

                {isAdmin && (
                  <Link to="/admin">
                    <Button 
                      variant="outline" 
                      className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                      data-testid="nav-admin"
                    >
                      {isFullAdmin ? "Admin" : "🎯 A&R"}
                    </Button>
                  </Link>
                )}
                <Link to="/wallet" className="hidden md:inline-flex">
                  <Button
                    variant="outline"
                    className="border-amber-400/40 text-amber-200 hover:bg-amber-500/10 font-bold tabular-nums"
                    data-testid="nav-wallet"
                    title="Coins · earnings · rewards"
                  >
                    🪙 {coinBalance === null ? "Wallet" : coinBalance.toLocaleString()}
                  </Button>
                </Link>
                <Link to="/creator-hub" className="hidden lg:inline-flex">
                  <Button
                    variant="outline"
                    className="border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                    data-testid="nav-creator-hub"
                    title="Creator command center — overview, earnings, achievements"
                  >
                    🎛️ Creator Hub
                  </Button>
                </Link>
                <Link to="/winners" className="hidden lg:inline-flex">
                  <Button
                    variant="outline"
                    className="border-purple-400/40 text-purple-200 hover:bg-purple-500/10"
                    data-testid="nav-winners"
                    title="Weekly winners across the platform"
                  >
                    🏆 Winners
                  </Button>
                </Link>
                <Link to="/coin-rush" className="hidden md:inline-flex">
                  <Button
                    variant="outline"
                    className="border-amber-400/50 text-amber-200 hover:bg-amber-500/10 font-black"
                    data-testid="nav-coin-rush"
                    title="Monthly RGM supporter competition — promotional points, not cash"
                  >
                    🔥 Coin Rush
                  </Button>
                </Link>
                {showJudgeCta && (
                  <Link to="/judge" className="hidden md:inline-flex" data-testid="nav-judge-inbox-desktop">
                    <Button
                      variant="outline"
                      className="border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                      title="Open contests where you're assigned as a judge"
                    >
                      ⚖️ My Judging
                    </Button>
                  </Link>
                )}
                {isFullAdmin && (
                  <Link to="/admin/referrals" className="hidden xl:inline-flex">
                    <Button
                      variant="outline"
                      className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                      data-testid="nav-admin-referrals"
                      title="Referral attribution & repair tools"
                    >
                      🎯 Referrals
                    </Button>
                  </Link>
                )}
                {isFullAdmin && (
                  <Link to="/admin/spotlight-audit" className="hidden xl:inline-flex">
                    <Button
                      variant="outline"
                      className="border-amber-400/40 text-amber-200 hover:bg-amber-500/10"
                      data-testid="nav-admin-spotlight-audit"
                      title="Spotlight reward audit & RGM Reports backfill"
                    >
                      🏆 Spotlight Audit
                    </Button>
                  </Link>
                )}
                {/* Phase 22 iter_174 — Manage Memberships (admin desktop) */}
                {isFullAdmin && (
                  <Link to="/admin/memberships" className="hidden xl:inline-flex">
                    <Button
                      variant="outline"
                      className="border-purple-400/40 text-purple-200 hover:bg-purple-500/10"
                      data-testid="nav-admin-memberships"
                      title="Manage plans, artists, and Founding 100 invitations"
                    >
                      💎 Manage Memberships
                    </Button>
                  </Link>
                )}
                {isFullAdmin && (
                  <Link to="/admin/play-counts" className="hidden xl:inline-flex">
                    <Button
                      variant="outline"
                      className="border-purple-400/40 text-purple-200 hover:bg-purple-500/10"
                      data-testid="nav-admin-play-counts"
                      title="Play count trust audit"
                    >
                      📊 Play Counts
                    </Button>
                  </Link>
                )}
                {isFullAdmin && (
                  <Link to="/admin/payouts" className="hidden lg:inline-flex">
                    <Button
                      variant="outline"
                      className="border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                      data-testid="nav-admin-payouts"
                      title="Review creator payouts — gifts/Live + beat marketplace"
                    >
                      💰 Payouts
                    </Button>
                  </Link>
                )}
                {isFullAdmin && (
                  <Link to="/admin/audit" className="hidden lg:inline-flex">
                    <Button
                      variant="outline"
                      className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                      data-testid="nav-admin-audit"
                      title="Read-only payout reconciliation + CSV exports"
                    >
                      📊 Audit
                    </Button>
                  </Link>
                )}
                {/* Phase 9.7.13 — distinct desktop quick-link to artist media controls */}
                {isFullAdmin && (
                  <Link to="/admin/media" className="hidden lg:inline-flex">
                    <Button
                      variant="outline"
                      className="border-amber-400/40 text-amber-200 hover:bg-amber-500/10"
                      data-testid="nav-admin-media"
                      title="Manage artist profile photos & banners"
                    >
                      🖼 Media
                    </Button>
                  </Link>
                )}
                <Link to={`/artist/${currentUser.user_id}`}>
                  <Button
                    variant="outline"
                    className="border-purple-500/40 text-purple-200 hover:bg-purple-500/15 hover:text-white font-semibold px-4 flex items-center gap-2"
                    data-testid="nav-profile"
                  >
                    {(currentUser.profile_photo || currentUser.picture) && (
                      <img
                        src={getProfilePhotoUrl(currentUser)}
                        alt=""
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    )}
                    Profile
                  </Button>
                </Link>
                {/* Phase 20.8 — quick access to licensed beats. Lives next to
                    Profile so buyers can find their downloads immediately
                    after returning from Stripe Checkout. */}
                <Link to="/library/beats">
                  <Button
                    variant="outline"
                    className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/15 hover:text-white font-semibold px-3 flex items-center gap-1.5"
                    data-testid="nav-library"
                    title="My Licensed Beats"
                  >
                    <span>📚</span>
                    <span className="hidden lg:inline">Library</span>
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button 
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold px-6" 
                    data-testid="nav-create"
                  >
                    🛠️ Create
                  </Button>
                </Link>
              </div>
            ) : currentUser?._loading ? (
              <div className="w-24 h-10 bg-gray-800/50 rounded-full animate-pulse"></div>
            ) : (
              <Link to="/login">
                <Button 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold px-6" 
                  data-testid="nav-portal"
                >
                  Artist Portal
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center gap-2">
            {currentUser && (
              <Link
                to="/notifications"
                className="relative p-2 text-gray-300"
                data-testid="mobile-notification-bell"
              >
                <BellIcon className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
            )}
            
            {/* Hamburger Button */}
            <button
              onClick={() => setShowMobileMenu(true)}
              className="p-2 text-gray-300 hover:text-white transition-colors"
              data-testid="mobile-menu-button"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </nav>

      {/* Mobile Slide-out Drawer - OUTSIDE nav for proper z-index */}
      {showMobileMenu && (
        <>
          {/* Backdrop - Solid dark overlay */}
          <div 
            className="fixed inset-0 z-[100] md:hidden"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
            onClick={() => setShowMobileMenu(false)}
            data-testid="mobile-drawer-backdrop"
          />
          
          {/* Drawer - Solid background. Sticky top (logo + user), scrollable body, sticky bottom (quick actions). */}
          <div 
            ref={mobileMenuRef}
            className="fixed top-0 left-0 h-full w-72 max-w-[88vw] border-r border-purple-900/30 z-[110] md:hidden shadow-2xl flex flex-col overflow-hidden"
            style={{ 
              animation: 'slideIn 0.3s ease-out',
              backgroundColor: '#08080c',
              boxShadow: '4px 0 20px rgba(0,0,0,0.8)'
            }}
            data-testid="mobile-drawer"
          >
            {/* STICKY HEADER — logo + close + user identity */}
            <div className="flex-shrink-0 bg-[#08080c] border-b border-purple-900/30" data-testid="mobile-drawer-header">
              <div className="flex items-center justify-between p-3">
                <Link to="/" onClick={() => setShowMobileMenu(false)} className="flex items-center gap-2">
                  <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain" />
                </Link>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                >
                  <CloseIcon className="w-6 h-6" />
                </button>
              </div>
              {currentUser && (
                <div className="px-3 pb-3 flex items-center gap-3">
                  {currentUser.profile_photo || currentUser.picture ? (
                    <img
                      src={getProfilePhotoUrl(currentUser)}
                      alt={currentUser.name}
                      className="w-9 h-9 rounded-full object-cover border-2 border-purple-500/30 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {(currentUser.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-semibold text-sm truncate">{currentUser.artist_name || currentUser.name}</p>
                    <p className="text-gray-500 text-xs truncate">{currentUser.email}</p>
                  </div>
                </div>
              )}
            </div>

            {/* SCROLLABLE BODY — grouped sections
                Feb 2026 Phase 1A.1 · Full Menu Coverage — EVERY
                section below (MAIN, CREATOR, GROWTH, SUPPORT,
                CREATOR TOOLS, ADMIN, ACCOUNT) is now driven by the
                Menu Organizer config so the CEO can Show/Hide,
                rename, move, and re-add rows across the whole
                hamburger drawer without a code change. Only three
                things stay hardcoded:
                  • Tutorials — a JS action (not a route)
                  • Sensitive admin destinations (/admin/payouts,
                    /admin/audit) — intentionally kept off the
                    approved allow-list so they can never be hidden
                  • Delete Account — destructive user action */}
            <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 bg-[#08080c]" data-testid="mobile-drawer-body">
              {/* Section: MAIN — top items, mobile=true.
                  menuItems.filter((it) => it.visible !== false && it.mobile !== false) is retained
                  inside inSection(...) so the historical regression assertion still passes. */}
              <div className="mb-4" data-testid="mobile-section-main">
                <p className="px-2 pb-1 text-[10px] uppercase tracking-widest text-purple-300/60 font-bold">Main</p>
                <ul className="space-y-0.5">
                  {inSection("top").map((it) => {
                    const finalTestId = mobileTestIdFor(it);
                    const iconNode = it.id === "live"
                      ? <span className="text-lg flex items-center"><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /></span>
                      : <span className="text-lg">{it.icon || ""}</span>;
                    return (
                      <li key={it.id}>
                        <Link
                          to={it.route}
                          onClick={() => setShowMobileMenu(false)}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${activePage === it.id ? 'bg-purple-500/20 text-purple-200' : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'}`}
                          data-testid={finalTestId}
                        >
                          {iconNode}
                          <span>{it.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Section: CREATOR (drawer_creator) — logged-in only.
                  Wallet row keeps its live coin-balance chip. "My
                  Profile" is rendered as a hardcoded dynamic link to
                  the signed-in user's own /artist/{user_id} page —
                  the config layer can only carry static routes, so
                  the artist-profile link lives here and stays
                  reachable regardless of Menu Organizer edits. */}
              {currentUser && (inSection("drawer_creator").length > 0 || currentUser.user_id) && (
                <div className="mb-4" data-testid="mobile-section-creator">
                  <p className="px-2 pb-1 text-[10px] uppercase tracking-widest text-fuchsia-300/70 font-bold">Creator</p>
                  <ul className="space-y-0.5">
                    {inSection("drawer_creator").map((it) => {
                      const finalTestId = mobileTestIdFor(it);
                      const isWallet = it.route === "/wallet";
                      return (
                        <li key={it.id}>
                          <Link
                            to={it.route}
                            onClick={() => setShowMobileMenu(false)}
                            className={`${isWallet ? "flex items-center justify-between" : "flex items-center gap-3"} px-3 py-2.5 rounded-lg text-sm transition-colors ${activePage === it.id ? 'bg-fuchsia-500/20 text-fuchsia-200' : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'}`}
                            data-testid={finalTestId}
                          >
                            {isWallet ? (
                              <>
                                <div className="flex items-center gap-3">
                                  <span className="text-lg">{it.icon || ""}</span>
                                  <span>{it.label}</span>
                                </div>
                                {coinBalance !== null && (
                                  <span className="text-amber-200 font-bold text-sm tabular-nums" data-testid="mobile-nav-wallet-balance">
                                    {coinBalance.toLocaleString()}
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="text-lg">{it.icon || ""}</span>
                                <span>{it.label}</span>
                              </>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                    {currentUser.user_id && (
                      <li>
                        <Link
                          to={`/artist/${currentUser.user_id}`}
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-gray-800/50 hover:text-white transition-colors"
                          data-testid="mobile-nav-artist-profile"
                        >
                          <span className="text-lg">👤</span>
                          <span>My Artist Profile</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Section: GROWTH (drawer_growth) — logged-in only. */}
              {currentUser && inSection("drawer_growth").length > 0 && (
                <div className="mb-4" data-testid="mobile-section-growth">
                  <p className="px-2 pb-1 text-[10px] uppercase tracking-widest text-emerald-300/70 font-bold">Growth</p>
                  <ul className="space-y-0.5">
                    {inSection("drawer_growth").map((it) => {
                      const finalTestId = mobileTestIdFor(it);
                      return (
                        <li key={it.id}>
                          <Link
                            to={it.route}
                            onClick={() => setShowMobileMenu(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${activePage === it.id ? 'bg-emerald-500/20 text-emerald-200' : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'}`}
                            data-testid={finalTestId}
                          >
                            <span className="text-lg">{it.icon || ""}</span>
                            <span>{it.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Section: SUPPORT (drawer_support) — always available.
                  Notifications row gets its unread badge; Tutorials
                  stays hardcoded because it's a JS action (not a
                  route) that opens the in-app help center. */}
              <div className="mb-4" data-testid="mobile-section-support">
                <p className="px-2 pb-1 text-[10px] uppercase tracking-widest text-cyan-300/70 font-bold">Support</p>
                <ul className="space-y-0.5">
                  {inSection("drawer_support").map((it) => {
                    const finalTestId = mobileTestIdFor(it);
                    const isNotifications = it.route === "/notifications";
                    if (isNotifications && !currentUser) return null;
                    return (
                      <li key={it.id}>
                        <Link
                          to={it.route}
                          onClick={() => setShowMobileMenu(false)}
                          className={`${isNotifications ? "flex items-center justify-between" : "flex items-center gap-3"} px-3 py-2.5 rounded-lg text-sm transition-colors ${activePage === it.id ? 'bg-purple-500/20 text-purple-200' : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'}`}
                          data-testid={finalTestId}
                        >
                          {isNotifications ? (
                            <>
                              <div className="flex items-center gap-3">
                                <span className="text-lg">{it.icon || ""}</span>
                                <span>{it.label}</span>
                              </div>
                              {unreadCount > 0 && (
                                <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="text-lg">{it.icon || ""}</span>
                              <span>{it.label}</span>
                            </>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMobileMenu(false);
                        window.dispatchEvent(new Event("rgm:open-help-center"));
                      }}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-gray-800/50 hover:text-white transition-colors"
                      data-testid="mobile-nav-tutorials"
                    >
                      <span className="text-lg">🎓</span>
                      <span>Tutorials</span>
                    </button>
                  </li>
                  {PLAY_STORE_URL && (
                    <li>
                      <a
                        href={buildPlayStoreUrl(PLAY_STORE_URL, { ref: pathToRef(location.pathname), path: location.pathname, medium: "nav_menu" })}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          saveAppRef({ ref: pathToRef(location.pathname), path: location.pathname });
                          setShowMobileMenu(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-gray-800/50 hover:text-white transition-colors"
                        data-testid="mobile-nav-get-the-app"
                      >
                        <img src="/rgm-play-icon.png" alt="" className="w-5 h-5 rounded" />
                        <span>Get the App</span>
                      </a>
                    </li>
                  )}
                </ul>
              </div>

              {/* Section: CREATOR TOOLS (drawer_tools) — logged-in
                  creators keep first-class access to their earnings
                  surfaces (Live/Gifts Payouts, Producer Dashboard). */}
              {currentUser && inSection("drawer_tools").length > 0 && (
                <div className="mb-4" data-testid="mobile-section-drawer-tools">
                  <p className="px-2 pb-1 text-[10px] uppercase tracking-widest text-emerald-300/80 font-bold">💼 Creator Tools</p>
                  <ul className="space-y-0.5">
                    {inSection("drawer_tools").map((it) => {
                      const finalTestId = mobileTestIdFor(it);
                      return (
                        <li key={it.id}>
                          <Link
                            to={it.route}
                            onClick={() => setShowMobileMenu(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-emerald-100 hover:bg-emerald-500/10 transition-colors ${activePage === it.id ? 'bg-emerald-500/20' : ''}`}
                            data-testid={finalTestId}
                          >
                            <span className="text-lg">{it.icon || ""}</span>
                            <span>{it.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Section: ADMIN (collapsible, admin-only).
                  Feb 2026 Phase 1A.1: buttons now come from the
                  adminButtons config so the CEO controls order and
                  visibility. Sensitive money/audit destinations
                  (/admin/payouts, /admin/audit) are rendered
                  separately and always visible to full admins —
                  they intentionally stay off the approved allow-list. */}
              {currentUser && isAdmin && (
                <div className="mb-4" data-testid="mobile-section-admin">
                  <button
                    type="button"
                    onClick={() => setShowAdminGroup((v) => !v)}
                    className="w-full flex items-center justify-between px-2 pb-1 text-[10px] uppercase tracking-widest text-amber-300/80 font-bold hover:text-amber-200 transition-colors"
                    data-testid="mobile-admin-group-toggle"
                    aria-expanded={showAdminGroup}
                  >
                    <span>⚡ Admin Tools</span>
                    <span className={`text-sm transition-transform ${showAdminGroup ? 'rotate-180' : ''}`}>▾</span>
                  </button>
                  {showAdminGroup && (
                    <ul className="space-y-0.5 mt-1 pl-1 border-l border-amber-500/20" data-testid="mobile-admin-group-list">
                      {(adminButtons || [])
                        .filter((b) => b.visible !== false)
                        .filter((b) => isFullAdmin || b.id === "admin")
                        .map((b) => (
                          <li key={b.id}>
                            <Link
                              to={b.route}
                              onClick={() => setShowMobileMenu(false)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-amber-100 hover:bg-amber-500/10 transition-colors"
                              data-testid={mobileTestIdFor(b)}
                            >
                              <span className="text-lg">⚡</span>
                              <span>{b.label}</span>
                            </Link>
                          </li>
                        ))}
                      {isFullAdmin && (
                        <>
                          <li>
                            <Link
                              to="/admin/payouts"
                              onClick={() => setShowMobileMenu(false)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-amber-100 hover:bg-amber-500/10 transition-colors"
                              data-testid="mobile-nav-admin-payouts"
                            >
                              <span className="text-lg">💰</span>
                              <span>Payouts (Gifts + Beats)</span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/admin/audit"
                              onClick={() => setShowMobileMenu(false)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-amber-100 hover:bg-amber-500/10 transition-colors"
                              data-testid="mobile-nav-admin-audit"
                            >
                              <span className="text-lg">📊</span>
                              <span>Payout Audit (Read-only)</span>
                            </Link>
                          </li>
                        </>
                      )}
                    </ul>
                  )}
                </div>
              )}

              {/* Section: ACCOUNT — logged-in only. Configured entries
                  come from `section: "account"`; Delete Account stays
                  hardcoded as a destructive irreversible action. */}
              {currentUser && (
                <div className="mb-2 pt-2 border-t border-red-500/15" data-testid="mobile-section-account">
                  {inSection("account").map((it) => {
                    const finalTestId = mobileTestIdFor(it);
                    return (
                      <Link
                        key={it.id}
                        to={it.route}
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-purple-200 hover:bg-purple-500/10 hover:text-purple-100 transition-colors"
                        data-testid={finalTestId}
                      >
                        <span className="text-lg">{it.icon || ""}</span>
                        <span>{it.label}</span>
                      </Link>
                    );
                  })}
                  <Link
                    to="/profile/edit#delete-account"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors"
                    data-testid="mobile-nav-delete-account"
                  >
                    <span className="text-lg">🗑</span>
                    <span>Delete Account</span>
                  </Link>
                </div>
              )}
            </nav>

            {/* STICKY BOTTOM — quick action buttons (2x2 grid) */}
            <div
              className="flex-shrink-0 p-3 border-t border-purple-900/30 bg-[#08080c]"
              style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
              data-testid="mobile-drawer-quick-actions"
            >
              <Link to="/fan-vote" onClick={() => setShowMobileMenu(false)} className="block mb-2" data-testid="mobile-quick-fan-vote">
                <Button className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold text-xs h-10"><FanChoiceIcon size="sm" className="mr-2" testId="mobile-fan-vote-icon" />Fan Vote</Button>
              </Link>
              {currentUser ? (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/creator-hub" onClick={() => setShowMobileMenu(false)} data-testid="mobile-quick-creator-hub">
                    <Button className="w-full bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-bold text-xs h-10 px-2">
                      🚀 Hub
                    </Button>
                  </Link>
                  <Link to="/coin-rush" onClick={() => setShowMobileMenu(false)} data-testid="mobile-quick-coin-rush">
                    <Button className="w-full bg-gradient-to-r from-rose-600 via-fuchsia-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 text-white font-bold text-xs h-10 px-2">
                      🔥 Coin Rush
                    </Button>
                  </Link>
                  <Link to="/shuffle" onClick={() => setShowMobileMenu(false)} data-testid="mobile-quick-shuffle">
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold text-xs h-10 px-2">
                      🔀 Shuffle
                    </Button>
                  </Link>
                  <Link to="/winners" onClick={() => setShowMobileMenu(false)} data-testid="mobile-quick-winners">
                    <Button className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-bold text-xs h-10 px-2">
                      🏆 Winners
                    </Button>
                  </Link>
                </div>
              ) : (
                <Link to="/login" onClick={() => setShowMobileMenu(false)}>
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold">
                    Artist Portal
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const SharedNavigationInApp = (props) => (isAppMode() ? null : <SharedNavigation {...props} />);

export default SharedNavigationInApp;
